import { useState } from 'react';
import { ArrowLeft, Scale, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthDataService, WeightEntryDto } from '../services/health-data.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface WeightTrackerProps {
  onBack: () => void;
}

export function WeightTracker({ onBack }: WeightTrackerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [weight, setWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Fetch weight entries
  const {
    data: entriesData,
    isLoading: entriesLoading,
  } = useQuery({
    queryKey: ['weight-entries', user?.id],
    queryFn: () => healthDataService.getWeightEntries({ limit: 50 }),
    enabled: !!user,
  });

  const entries = entriesData?.data || entriesData || [];

  // Create weight entry mutation
  const createMutation = useMutation({
    mutationFn: (data: WeightEntryDto) => healthDataService.createWeightEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weight-entries'] });
      toast.success('Weight logged successfully!');
      setWeight('');
      setNotes('');
      setShowForm(false);
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!weight || isNaN(Number(weight))) {
      toast.error('Please enter a valid weight');
      return;
    }

    createMutation.mutate({
      weight: Number(weight),
      date: new Date().toISOString(),
      notes: notes.trim() || undefined,
    });
  };

  if (entriesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Weight Tracking</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-gradient-to-r from-dropsugar-blue to-dropsugar-blue-dark text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all mb-8"
          >
            + Add Weight Entry
          </button>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">New Weight Entry</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight (kg) *
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="Enter weight in kg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                  required
                  min="0"
                  step="0.1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Weight'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setWeight('');
                    setNotes('');
                  }}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Scale className="w-6 h-6 mr-2 text-dropsugar-green" />
            Weight History
          </h2>
          {entries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No weight entries yet. Add your first entry above!</p>
          ) : (
            <div className="space-y-3">
              {entries.map((entry: any, index: number) => {
                const date = new Date(entry.date || entry.createdAt);
                const previousWeight = index < entries.length - 1 ? entries[index + 1].weight : null;
                const change = previousWeight ? (entry.weight - previousWeight).toFixed(1) : null;
                return (
                  <div key={entry.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl font-bold text-gray-900">{entry.weight} kg</span>
                          {change && (
                            <span className={`text-sm font-semibold ${Number(change) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {Number(change) > 0 ? '↑' : '↓'} {Math.abs(Number(change))} kg
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {date.toLocaleDateString()} {date.toLocaleTimeString()}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-gray-500 mt-1">{entry.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
